import React, { useState } from 'react'
import { useIntl, FormattedMessage } from 'react-intl'
import { useDocuments } from 'contexts/documents'
import { useAccess } from 'contexts/access'
import { useMe } from 'contexts/me'
import { useCombineAccessUnits, splitAccessUnits } from 'pages/documents/utilities'
import { useCategories } from 'pages/documents/utilities'
import { splitName, extensionsByMime } from 'utilities/file'
import { size } from 'utilities/object'
import { getListRepresentationFromProfile } from 'utilities/person'
import { removeNoneCategoryFromPayload } from 'pages/documents/utilities'
import { ModalHeader } from 'modals/generic'
import Form from 'components/form/controller'
import StringField from 'components/form/field/string'
import SelectField from 'components/form/field/select'
import PersonField from 'components/form/field/person'
import AccessUnitsField from 'components/form/field/access-units'
import QuickSelectAccessUnits from 'pages/documents/components/quick-select-access-units'
import CheckboxField from 'components/form/field/checkbox'
import Caption from 'components/typography/caption'
import { FileField, Meta, FileNameIcon } from './s'
import Message from 'components/message'
import Actions from 'components/form/actions'
import { Plain, ButtonSubmit } from 'components/button'

const UploadDocument = ({
    file = null,
    id: documentRefId = null,
    category = null,
    concerns: outsideConcerns = null,
    shares: outsideShares = [],
    owners: outsideOwners = [],
    signing: outsideSigning = false,
    setConfiguringSigning,
    modal, onDone,
    salt
}) => {
    const { formatMessage } = useIntl()

    const { uploadDocument } = useDocuments()

    const { checkFeature, check } = useAccess()
    const signingAccess = checkFeature('digital-signing') && (check('digital-signing:manage')/* || check('unit:document-signing:manage')*/)

    const {
        getListMe,
        isItMyOwnId
    } = useMe()

    const combineAccessUnits = useCombineAccessUnits()

    const [uploading, setUploading] = useState(false)
    const [added, setAdded] = useState(false)

    const [concerns, setConcerns] = useState(outsideConcerns ?
        getListRepresentationFromProfile(outsideConcerns) :
        null
    )

    const myOwnerAccessUnit = {
        unit: getListMe(),
        access: {
            share: true,
            owner: true
        },
        locked: true
    }

    const [whistle, setWhistle] = useState(Date.now())
    const [access, setAccess] = useState(() => {
        const value = (outsideShares.length || outsideOwners.length) ?
            [
                ...combineAccessUnits({
                    shares: outsideShares,
                    owners: outsideOwners
                }).filter(({ unit }) => !isItMyOwnId(unit.id)),
                myOwnerAccessUnit
            ] :
            [myOwnerAccessUnit]

        return {
            value,
            changed: false
        }
    })

    const [signing, setSigning] = useState(outsideSigning)
    const [signatories, setSignatories] = useState(accessUnitsToSignatories(access.value))

    const { categories } = useCategories()

    const upload = async (body, { resetTouched }) => {
        if(uploading) {
            return
        }

        setUploading(true)

        body = removeNoneCategoryFromPayload(body)

        const {
            shares,
            owners
        } = splitAccessUnits(access.value)

        body.delete('access')
        body.set('owners', JSON.stringify(owners))
        !!shares.length && body.set('shares', JSON.stringify(shares))

        if(file) {
            body.set('type', 'file')
            body.set('file', file.blob, `${body.name}.pdf`)

            if(documentRefId) {
                body.set('documentRefId', documentRefId)
            }

            const { ok, response: dokument } = await uploadDocument(body)

            if(ok) {
                if(!!signing) {
                    setConfiguringSigning({
                        dokument: {
                            ...dokument,
                            signing: {
                                signatories
                            }
                        }
                    })
                } else {
                    setAdded(true)
                }

                onDone?.(dokument)
                modal.dismiss()
            }
        } else {
            const { ok, response: dokument } = await uploadDocument(body)

            if(ok && dokument) {
                resetTouched()

                if(!!signing) {
                    setConfiguringSigning({ dokument })
                }

                if(onDone) {
                    onDone()
                } else {
                    modal.dismiss()
                }
            }
        }

        setUploading(false)
    }

    const fields = {}
    const noOwners = !access.value.some(({ access }) => access.owner)

    return (
        <>
            <ModalHeader
                heading={formatMessage({
                    id: file ?
                        'document_action_add_to_documents' :
                        'document_action_add',
                    defaultMessage: file ?
                        'Add to documents ' :
                        'Add document'
                })}
                dismiss={modal.dismiss} />
            <Form
                mode="multipart"
                layout="vertical"
                onSubmit={upload}
                key={`${salt}:upload`}>
                {({ touched, values, errors, trigger }) => {
                    const hasAddedFile = !!('file' in values) && !('file' in errors)

                    const nameParts = splitName(values?.file?.name)
                    fields.name = file?.name ?? nameParts?.name
                    fields.extension = file ? 'pdf' : nameParts?.extension

                    return (
                        <>
                            {!file && (
                                <FileField
                                    salt={salt}
                                    label={false}
                                    text={formatMessage({
                                        id: 'document_upload_instruction_single',
                                        defaultMessage: 'Upload your document here'
                                    })}
                                    name="file"
                                    field={{ required: true }}
                                    accept={extensionsByMime}
                                    multiple={false} />
                            )}
                            {(!!hasAddedFile || (file && !added)) && (
                                <>
                                    {file && (
                                        <Meta>
                                            <FileNameIcon
                                                name={values.name}
                                                extension={fields.extension}
                                                ellipsify={false} />
                                        </Meta>
                                    )}
                                    <StringField
                                        salt={salt}
                                        label={formatMessage({
                                            id: 'document_label_name',
                                            defaultMessage: 'Name of document'
                                        })}
                                        name="name"
                                        field={{
                                            value: fields.name,
                                            suffix: fields.extension ? `.${fields.extension}` : null,
                                            include: 'always',
                                            required: true
                                        }}
                                        controlProps={{ autoFocus: true }}
                                        key={`${salt}:name:${fields.name}:${fields.extension}`} />
                                    <SelectField
                                        salt={salt}
                                        label={formatMessage({
                                            id: 'noun_category',
                                            defaultMessage: 'Category'
                                        })}
                                        name="category"
                                        field={{
                                            value: category,
                                            include: !!category ?
                                                'always' :
                                                'touched',
                                            allowEmptyOption: false
                                        }}
                                        options={categories.map(category => ({
                                            ...category,
                                            key: category.value
                                        }))} />
                                    <PersonField
                                        salt={salt}
                                        label={formatMessage({
                                            id: 'document_label_regarding',
                                            defaultMessage: 'Regarding'
                                        })}
                                        name="concerns"
                                        field={{
                                            value: concerns,
                                            include: 'always',
                                            optional: formatMessage({
                                                id: 'document_label_regarding_optional',
                                                defaultMessage: 'Optional: Specify who the document regards'
                                            })
                                        }}
                                        picker={{ outer: false }}
                                        onChange={({ concerns }) => setConcerns(concerns)} />
                                    <AccessUnitsField
                                        salt={salt}
                                        label={formatMessage({
                                            id: 'noun_access',
                                            defaultMessage: 'Access'
                                        })}
                                        name="access"
                                        field={{
                                            value: access.value,
                                            include: access.changed ?
                                                'always' :
                                                'touched'
                                        }}
                                        formatAdminTooltip={unit => formatMessage({
                                            id: 'document_unit_administrator_description',
                                            defaultMessage: 'Users with roles that grant document access in <strong>{name}</strong>'
                                        }, unit)}
                                        {...(concerns ? {
                                            belowSearchContent: (
                                                <QuickSelectAccessUnits
                                                    options={[{
                                                        unit: concerns,
                                                        access: {
                                                            share: true,
                                                            owner: false
                                                        }
                                                    }]}
                                                    values={access.value}
                                                    insertValue={value => {
                                                        setAccess({
                                                            value: [
                                                                value,
                                                                ...access.value
                                                            ],
                                                            changed: true
                                                        })

                                                        setWhistle(Date.now())
                                                    }} />
                                            )
                                        } : null)}
                                        onChange={({ access }) => {
                                            setAccess({
                                                value: access,
                                                changed: true
                                            })

                                            setSignatories(accessUnitsToSignatories(access))
                                        }}
                                        whistle={whistle} />
                                    {(access.changed && !!noOwners) && (
                                        <Message
                                            type="warning"
                                            message={formatMessage({
                                                id: 'document_access_owners_missing_warning',
                                                defaultMessage: 'At least one unit needs to have <strong>See and edit<\/strong> access.'
                                            })} />
                                    )}
                                    {!!signingAccess && (
                                        <CheckboxField
                                            salt={salt}
                                            label={formatMessage({
                                                id: 'document_action_send_for_signing',
                                                defaultMessage: 'Send for signing'
                                            })}
                                            helper={formatMessage({
                                                id: 'document_action_send_for_signing_helper',
                                                defaultMessage: 'Only available for PDFs'
                                            })}
                                            name="signing"
                                            field={{
                                                value: signing,
                                                include: 'never'
                                            }}
                                            interaction="switch"
                                            enabled={(fields.extension === 'pdf' && !uploading)}
                                            onChange={({ signing }) => setSigning(signing)} />
                                    )}
                                    <CheckboxField
                                        salt={salt}
                                        label={formatMessage({
                                            id: 'notification_toggle_document_upload_label',
                                            defaultMessage: 'Send notification when uploading'
                                        })}
                                        name="notify"
                                        field={{
                                            value: true,
                                            include: 'always'
                                        }}
                                        interaction="switch"
                                        enabled={!uploading} />
                                    {!!values?.notify && (
                                        <Caption>
                                            <FormattedMessage
                                                id="notification_toggle_document_upload_description"
                                                defaultMessage="A notification will be sent to those the document will be shared with and who has opted to receive notifications." />
                                        </Caption>
                                    )}
                                </>
                            )}
                            {(!file || (file && !added)) && (
                                <Actions className="compact">
                                    <Plain
                                        onClick={modal.dismiss}
                                        className="neutral"
                                        disabled={uploading}>
                                        <FormattedMessage
                                            id="action_cancel"
                                            defaultMessage="Cancel" />
                                    </Plain>
                                    <ButtonSubmit
                                        className={`constructive${uploading ? ' loading' : ''}`}
                                        disabled={(!touched.length && !file) || !!size(errors) || (!hasAddedFile && !file) || uploading}
                                        ref={trigger}>
                                        <FormattedMessage
                                            id={signing ?
                                                'document_action_setup_signing' :
                                                'action_upload'}
                                            defaultMessage={signing ?
                                                'Setup signing' :
                                                'Upload'
                                            } />
                                    </ButtonSubmit>
                                </Actions>
                            )}
                            {!!added && (
                                <>
                                    <Message
                                        type="success"
                                        className="compact"
                                        message={formatMessage({
                                            id: 'documents_message_contract_added',
                                            defaultMessage: 'The contract has been added to documents'
                                        })} />
                                    <Actions className="centered">
                                        <Plain
                                            onClick={modal.dismiss}
                                            className="neutral">
                                            <FormattedMessage
                                                id="action_done"
                                                defaultMessage="Done" />
                                        </Plain>
                                    </Actions>
                                </>
                            )}
                        </>
                    )
                }}
            </Form>
        </>
    )
}

const accessUnitsToSignatories = accessUnits => accessUnits
    .filter(({ unit }) => unit.type === 'user')
    .map(({ unit }) => ({
        user: unit,
        type: 'user'
    }))

export default UploadDocument